import { _GettersTree, defineStore } from 'pinia';
import { Goal } from "@/models/Goal.model";
import { GoalSource, PopulatedGoalStructureContent } from "@/utils/GoalSource";
import { GOAL_SCOPES, GoalFilterDef, LESSON_TYPES } from "@/stores/filters/GoalFilterDef";
import { AbstractBaseStore, AbstractStoreState } from "@/stores/AbstractBase.store.ts";
import { Curriculum } from "@/models/Curriculum.model.ts";
import { useOrganisationsStore } from "@/stores/Organisations.store.ts";
import { Classroom } from "@/models/Classroom.model";
import { goalBelongsToMainGroup } from "@/goals/utils/GoalMainGroup.util";

export enum GoalContext {
	IMPORT = 'import',
	LIST = 'list',
}

interface GoalStoreState extends AbstractStoreState<Goal, GoalFilterDef> {
	loadingPromise: Promise<void> | null,
	curriculum: Curriculum | null,
	usedTags: string[],
	sources: Record<string, GoalSource>,
	collectionId: string | null,
	context: GoalContext,
}

class GroupedGoals {
	constructor(
		public title: string = '',
		public path: string = '',
		public color: string | null = null,
		public index: number = 0,
		public goals: GoalSearchResult[] = [],
	) {}
}

const BaseStore = AbstractBaseStore<Goal, GoalStoreState, GoalFilterDef>();

const forks = new Map<string, any>();

export class GoalSearchResult {

	public highlightedTitle: string | null = null;
	public highlightedCustomTitle: string | null = null;

	public goal: Goal;

	constructor(goal: Goal) {
		this.goal = goal;
	}
}

export function useForkedGoalsStore(name: string) {

	if (!forks.has(name)) {
		forks.set(name, defineStore(name, {

			state: (): GoalStoreState => {

				const initialFilter = new GoalFilterDef();
				const defaultFilter = new GoalFilterDef();

				return BaseStore.state({
					curriculum: null,
					collectionId: null,
					initialFilter,
					defaultFilter,
					usedTags: [],
					sources: {},
					loadingPromise: null,
					context: GoalContext.LIST,

					filter: initialFilter,
				});

			},

			getters: {

				...BaseStore.getters,

				apiPath(state) {
					if (state.collectionId) {
						return '/goal-collections/' + state.collectionId + '/goals';
					} else {
						return '/goals';
					}
				},

				/*
				Get only the goals that belong to the currently set curriculum.
				When no curriculum is set (multi-curriculum mode), return all items.
				*/
				curriculumItems(state): Goal[] {

					if(state.curriculum) {
						return state.items.filter((goal) => {
							if(!goal.source || goal.source !== state.curriculum!.id) {
								return false;
							}
							return true;
						});
					} else {
						return state.items;
					}
				},

				filteredItems(state): GoalSearchResult[] {

					const filter = state._filter;
					if (!this.curriculumItems.length) {

						return this.curriculumItems.map((goal: Goal) => new GoalSearchResult(goal));

					} else if(!filter || filter.isEmpty()) {

						// clear any highlighted keywords
						return this.curriculumItems.map(
							(goal: Goal) => {
								return new GoalSearchResult(goal)
							}
						);

					}

					const keywords = filter.getParamValues('keywords').map((filterValue) => filterValue.id.toLowerCase());
					const tags = filter.getParamValues('tags').map((filterValue) => filterValue.id);
					const mainGroups = filter.getParamValues('mainGroups').map((filterValue) => filterValue.id.toLowerCase());

					const searchResults: GoalSearchResult[] = [];
					this.curriculumItems.forEach((goal: Goal): void => {

						if (mainGroups.length > 0) {
							const matchingGroup = mainGroups.some((mainGroup) => goalBelongsToMainGroup(goal, mainGroup));
							if (!matchingGroup) {
								return;
							}
						}

						if (tags.length > 0) {
							// AND logic
							// let allTagsMatch = true;
							// for(let i = 0; i < tags.length; i++) {
							// 	if(!goal.tags || !goal.tags.includes(tags[i])) {
							// 		allTagsMatch = false;
							// 		break;
							// 	}
							// }
							// if(!allTagsMatch) {
							// 	return false;
							// }

							// OR logic
							let someTagsMatch = false;
							for (let i = 0; i < tags.length; i++) {
								if (goal.tags && goal.tags.includes(tags[i])) {
									someTagsMatch = true;
									break;
								}
							}
							if (!someTagsMatch) {
								return;
							}
						}

						let highlightedTitle = null;
						let highlightedCustomTitle = null;

						if (keywords.length > 0) {
							let allKeywordsMatch = true;
							for (let i = 0; i < keywords.length; i++) {

								const regex = new RegExp(keywords[i], 'gi');

								let sourcePathMatches, titleMatches, customTitleMatches = null;
								sourcePathMatches = goal.fullPath.match(regex);
								if (!sourcePathMatches) {
									titleMatches = goal.title && goal.title.match(regex);
									if (goal.customTitle) {
										customTitleMatches = goal.customTitle.match(regex);
									}
								}

								if (!sourcePathMatches && !titleMatches && !customTitleMatches) {

									allKeywordsMatch = false;
									break;

								} else {

									// escape any html entities in the title variant with the highlighted keywords so it can
									// be safely rendered as HTML, only rendering the tags used for highlighting
									const elForHtmlConversion = document.createElement('p');

									if (titleMatches) {
										if (highlightedTitle === null) {
											if (goal.title) {
												elForHtmlConversion.textContent = goal.title;
											}
											highlightedTitle = elForHtmlConversion.innerHTML;
										}

										highlightedTitle = highlightedTitle.replace(regex, (match) => {
											return '<em>' + match + '</em>';
										});
									}

									if (customTitleMatches) {
										if(highlightedCustomTitle === null) {
											if (goal.customTitle) {
												elForHtmlConversion.textContent = goal.customTitle;
											}
											highlightedCustomTitle = elForHtmlConversion.innerHTML;
										}

										highlightedCustomTitle = highlightedCustomTitle.replace(regex, (match) => {
											return '<em>' + match + '</em>';
										});
									}
								}

							}

							if (!allKeywordsMatch) {
								return;
							}
						}

						const result = new GoalSearchResult(goal);
						result.highlightedTitle = highlightedTitle;
						result.highlightedCustomTitle = highlightedCustomTitle;

						searchResults.push(result);
					});

					return searchResults;
				},

				/**
				 * @returns number
				 */
				defaultRecordsPerPage(): number {
					return 5000;
				},

				defaultMask(state) {
					const mask = [
						'*',
						'tags.*',
					];

					// Only load evaluations if filter has pupil ids set
					if (state._filter?.pupilIds.length) {
						return [
							'*',
							'goalCollections.id',
							'goalCollections.customTitle',
							'evaluations.*',
							'evaluations.author.name',
							'evaluations.evaluationLevel.*',
							'evaluations.pupil.id',
							'tags.*'
						];
					}

					return mask;
				},

				/**
				 * Returns the GoalSource instances relevant to the current context:
				 * - when a curriculum is set (single-curriculum context, e.g. the goal selector
				 *   or a grade-level goal collection) only that curriculum's source is returned;
				 * - otherwise (multi-curriculum context, e.g. insights/evaluation) all loaded
				 *   sources are returned.
				 *
				 * Always returns an array — callers must never assume a single source.
				 */
				allSources(state): GoalSource[] {
					if (state.curriculum) {
						const key = Object.keys(state.sources).find(
							k => k.toLowerCase() === state.curriculum!.id.toLowerCase()
						);
						return key ? [state.sources[key]] : [];
					}
					return Object.values(state.sources);
				},

				structuredGoals(state): PopulatedGoalStructureContent[] {
					return this.calculateGoalStructures(this.filteredItems);
				},

				/**
				 * Calculate the structure of a provided list of goal items (or goal search results)
				 * @param state
				 */
				calculateGoalStructures(state): (filteredItems: GoalSearchResult[]) => PopulatedGoalStructureContent[] {
					return (filteredItems) => {

						// Get unique source IDs from the filtered items
						const sourceIds = [...new Set(
							filteredItems
								.map(r => r.goal.source)
								.filter(Boolean)
						)] as string[];

						if (sourceIds.length === 0) {
							// Fall back to first loaded source for empty-state display
							const keys = Object.keys(state.sources);
							if (keys.length > 0) {
								return [ state.sources[keys[0]].getPopulatedStructure([]) ];
							}
							return [ new PopulatedGoalStructureContent('') ];
						}

						// For each loaded source, return a populated PopulatedGoalStructureContent.
						// Source IDs whose source is not (yet) loaded are dropped: their goals
						// cannot be displayed without the source's structure, and emitting empty
						// ''-keyed structures instead broke v-for keys when several were missing.
						return sourceIds
							.map(sourceId => {

								const source = this.getSourceForId(sourceId);
								if (!source) {
									return null;
								}

								const goalsForSource = filteredItems.filter(
									r => r.goal.source?.toLowerCase() === sourceId.toLowerCase()
								);
								return source.getPopulatedStructure(goalsForSource);

							})
							.filter((s): s is PopulatedGoalStructureContent => s !== null);

					};
				}
			},

			actions: {

				...BaseStore.actions,

				setCurriculum(
					curriculum: Curriculum | null,
					collectionId: string | null = null,
				) {
					this.curriculum = curriculum;
					this.collectionId = collectionId;

					// reset — drop any previously loaded sources so a context switch never
					// leaks structures from another curriculum (the store is shared/forked).
					this.loadedOnce = false;
					this.items = [];
					this.sources = {};
				},

				/**
				 * Create the default filter set that is used when loading goals WITH evaluations.
				 * @param classroom The classroom to scope to, or null for the school-wide view.
				 */
				getDefaultEvaluationsFilter(classroom: Classroom | null) {

					const organisation = useOrganisationsStore().getCurrentOrganisation();

					const initialFilter = new GoalFilterDef();
					initialFilter.goalScopes = [ GOAL_SCOPES.PLANNED ];
					initialFilter.lessonTypes = [ LESSON_TYPES.COLLECTIVE, LESSON_TYPES.ACTIVITIES, LESSON_TYPES.THEMES ];
					initialFilter.classroomIds = classroom?.id ? [ classroom.id.toString() ] : [];

					// Default to the current school year
					const schoolYearInterval = organisation.getCurrentSchoolYearInterval();
					if (!schoolYearInterval) {
						throw new Error('No school year interval set for organisation');
					}

					if (schoolYearInterval.start) {
						initialFilter.start = schoolYearInterval.start.toJSDate();
					}

					if (schoolYearInterval.end) {
						initialFilter.end = schoolYearInterval.end.toJSDate();
					}

					return initialFilter.clone();
				},

				new() {
					return new Goal();
				},

				async loadWithSource(forceReload: boolean = false) {
					// Load items first, then load sources based on the items' source field
					await (forceReload ? this.load() : this.loadOnce());
					await this.loadSources();
				},

				/**
				 * Find the GoalSource for a given source ID (case-insensitive).
				 */
				getSourceForId(sourceId: string): GoalSource | null {
					const key = Object.keys(this.sources).find(
						k => k.toLowerCase() === sourceId.toLowerCase()
					);
					return key ? this.sources[key] : null;
				},

				async loadSourcesForGoals(goals: Goal[]) {
					const toLoad: { id: string; source: GoalSource }[] = [];

					// Collect unique source IDs from loaded items
					const sourceIds = [...new Set(
						goals.map((g: Goal) => g.source).filter(Boolean)
					)] as string[];

					// Also include the curriculum source if set
					if (this.curriculum && !sourceIds.some(
						id => id.toLowerCase() === this.curriculum!.id.toLowerCase()
					)) {
						sourceIds.push(this.curriculum.id);
					}

					for (const sourceId of sourceIds) {
						// Check if already loaded (case-insensitive)
						const existingKey = Object.keys(this.sources).find(
							k => k.toLowerCase() === sourceId.toLowerCase()
						);

						if (!existingKey) {
							toLoad.push({ id: sourceId, source: new GoalSource() });
						}
					}

					if (toLoad.length === 0) return;

					await Promise.all(
						toLoad.map(t => t.source.load(new Curriculum(t.id)))
					);

					const newSources = { ...this.sources };
					for (const t of toLoad) {
						newSources[t.id] = t.source;
					}
					this.sources = newSources;
				},

				/**
				 * Load all GoalSource instances needed for the current items.
				 * Also loads the source for the currently set curriculum if applicable.
				 */
				async loadSources() {
					await this.loadSourcesForGoals(this.items);
				},

				/**
				 * Set the context for the store.
				 * This can either be 'list' or 'import'.
				 * When context is set to 'import', the store will not remove items from the list when
				 * items are removed from the selected goal selection. Instead, the ID will be set to NULL,
				 * resulting in the 'selected' checkbox in the list being unchecked.
				 *
				 * When context is set to 'list', the store will remove items from the store when items are removed,
				 * resulting in the goal being removed from the visible list.
				 *
				 * @param context
				 */
				setContext(context: GoalContext) {
					this.context = context;
				},

				resetContext() {
					this.context = GoalContext.LIST;
				},

				mapToItem(item: any): Goal {
					return Goal.createFromApiData(item);
				},

				async afterLoad(items: Goal[]) {
					await this.loadSources();
					this.updateUsedTags();
					this.sortItems();
				},

				async saveItem(goal:Goal) {
					return this.bulkSave([goal]);
				},

				supplementWithGoalsFromSource() {

					// Only supplement with sources relevant to the current context (allSources is
					// curriculum-aware), so a single-curriculum selector never pulls in goals from
					// another curriculum that happens to be loaded in the shared store.
					const sources = this.allSources;
					if (sources.length === 0) {
						throw new Error('No source set');
					}

					const pathsAlreadyInList = this.items.map((item) => item.source + '|' + item.fullPath);

					// Iterate over the relevant sources and supplement with their goals
					for (const source of sources) {
						const goals = source.getGoals();

						// @TODO this will not work properly for goals that have no 'code' (and as such have the same 'fullPath')
						// No big issue right now since only used for our own importer which relies on our own data structure (which requires a code for each goal)
						const goalsToAdd = goals.filter((goal) => !pathsAlreadyInList.includes(goal.source + '|' + goal.fullPath));
						this.items.push(...goalsToAdd);

						// Track added items to avoid duplicates across sources
						goalsToAdd.forEach((goal) => pathsAlreadyInList.push(goal.source + '|' + goal.fullPath));
					}

					this.sortItems();

				},

				sortItems() {

					this.items = sortGoalsByPath(this.items);

				},

				removeNonPersistedItems() {
					this.items = this.items.filter((goal) => !!goal.id);
				},

				onStoreChanged() {
					// When the store changes, we need to update the used tags
					this.updateUsedTags();
				},

				/**
				 * Update the usedTags set with the tags from all the goals in the store
				 */
				updateUsedTags(): void {

					const usedTagSet = new Set<string>();
					this.curriculumItems.forEach((goal: Goal) => {
						if (!goal.imported) {
							return;
						}

						if(goal.tags) {
							goal.tags.forEach((tag: string) => usedTagSet.add(tag));
						}
					});

					// Convert to array
					this.usedTags = Array.from(usedTagSet);

					// Sort on name
					this.usedTags.sort((a: string, b: string) => a.localeCompare(b));
				},

				/**
				 * Remove a loaded item from the store
				 * (does not affect entries in the backend)
				 * @param model
				 */
				removeStoreItem(model: Goal) {
					const index = this.items.findIndex((item) => item.id === model.id);
					if (index >= 0) {

						switch (this.context) {
							case GoalContext.IMPORT:
								// In import mode, we do not remove the item from the list, but unset its ID
								// so it will still be visible in the importer
								this.items[index].id = null;
								break;
							case GoalContext.LIST:
							default:
								// In list mode, we remove the item from the list
								this.items.splice(index, 1);
								break;
						}
					}
					this.scheduleOnStoreChangedDispatch();
				},

				/*
				 * Apply a new filter and, if required, reload the store.
				 * @param filter
				 */
				async applyFilter(filter: GoalFilterDef, forceReload: boolean = false) {

					// We don't want to reload the store data if the filter change only contains parameters
					// that are filtered out client side (for example searching for keywords etc)
					// The filter will decide if the data should be reloaded or not.
					// If the store has not been loaded once yet, we always reload the store.
					const isReloadRequired = forceReload || !this.loadedOnce || !this._filter || this._filter.requiresFreshServerData(filter);

					this._filter = filter.clone();

					if (isReloadRequired) {
						await this.loadWithSource(true);
					}
				},

				findParentGoal(goal: Goal): Goal | null {

					// Parents MUST have a sourcePath and sourcePathId
					const parent = this.items.find(v => {
						if (!v.sourceParentPath || !v.sourcePathId) {
							return false;
						}

						return (v.sourceParentPath + '.' + v.sourcePathId) === goal.sourceParentPath +
							(goal.sourcePathId ?  + '.' + goal.sourcePathId : '');
					});

					if (parent) {
						return parent;
					}
					return null;

				},

				resetFilter() {
					if (this._defaultFilter) {
						this.applyFilter(this._defaultFilter.clone());
					}
				},

				/**
				 * This check is used to see if two models are considered 'the same', from a backend point of view.
				 * This is used to determine if a model should be updated or added to the store.
				 *
				 * Check if two goals are equals.
				 *
				 * This means:
				 * - both imported in database and have the same id (= imported goals)
				 * - both have the same sourceId (if both have one) (= most goal pickers)
				 * - both have the same sourcePath and sourcePathId (= fallback if goal picker doesn't have sourceId)
				 * - both have the same sourcePath and no sourcePathId (= fallback if goal picker doesn't have sourceId)
				 *
				 * @param storeItem
				 * @param other
				 */
				representsSameModel(storeItem: Goal, other: Goal)
				{
					// different source? no match!
					if (storeItem.source !== other.source) {
						return false;
					}

					// same database id (if both have one)? match! (or not)
					if (
						storeItem.id && other.id
					) {
						return storeItem.id === other.id;
					}

					// same sourceId (if both have one)? match! (or not)
					if(storeItem.sourceId && other.sourceId) {
						return storeItem.sourceId === other.sourceId;
					}

					// same sourcePath + sourceId? match!
					if (
						(storeItem.sourceParentPath || other.sourceParentPath) &&
						(storeItem.sourcePathId || other.sourcePathId) &&
						storeItem.sourceParentPath === other.sourceParentPath &&
						storeItem.sourcePathId === other.sourcePathId
					) {
						return true;
					}

					// same sourcePath and no sourceId? match!
					if (
						(storeItem.sourceParentPath || other.sourceParentPath) &&
						(!storeItem.sourcePathId && !other.sourcePathId) &&
						storeItem.sourceParentPath === other.sourceParentPath
					) {
						return true;
					}

					return false;
				},

			},

		}));
	}

	return forks.get(name);

}

/**
 * Sort a list of goals by their full path.
 * @param goals
 * @returns
 */
export function sortGoalsByPath(goals: Goal[])
{
	const items = goals.slice();

	// create a map with all paths for performance
	const fullPaths = new Map<Goal, string[]>();
	items.forEach((goal) => {
		fullPaths.set(goal, goal.fullPath.split('.'));
	});

	// sort the local copy of the items
	items.sort((a, b) => {

		const segmentsA = fullPaths.get(a) as string[];
		const segmentsB = fullPaths.get(b) as string[];

		const minLength = Math.min(segmentsA.length, segmentsB.length);

		for (let i = 0; i < minLength; i++) {
			if (segmentsA[i] !== segmentsB[i]) {
				return segmentsA[i].localeCompare(segmentsB[i], undefined, { numeric: true });
			}
		}

		return segmentsA.length - segmentsB.length;
	});

	return items;
}

export const useGoalsStore = useForkedGoalsStore('goals');
